import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { quickRange, type DateRange } from '@/lib/utils'

const STORAGE_KEY = 'l2s:rewardRange'
const EMPTY: DateRange = { from: '', to: '' }

function readStored(): DateRange {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    // Prvá návšteva: prebiehajúca sezóna. Prázdne obdobie je „celé obdobie“.
    if (!raw) return quickRange('season')
    const parsed = JSON.parse(raw) as Partial<DateRange>
    return { from: parsed.from ?? '', to: parsed.to ?? '' }
  } catch {
    // A corrupted entry must never take the payout screen down.
    return EMPTY
  }
}

function writeStored(range: DateRange) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(range))
  } catch {
    /* private mode / quota - the URL still carries the range */
  }
}

/**
 * The period filter shared by the reward overview and one instructor's payout
 * sheet, so stepping to "September" on one screen and opening the other keeps
 * September.
 *
 * The URL is the source of truth - that keeps the view reloadable and
 * shareable, and it is what carries the range through the link from the list
 * into a detail. `localStorage` only fills in the gap when a screen is entered
 * without any parameters at all, e.g. from the main menu.
 *
 * An explicitly cleared range is stored as empty, so "celé obdobie" is
 * remembered as a choice rather than being overwritten by the last period.
 */
export function useRewardRange(): [DateRange, (next: DateRange) => void] {
  const [params, setParams] = useSearchParams()

  const fromUrl = params.get('from')
  const toUrl = params.get('to')
  const hasUrlRange = fromUrl !== null || toUrl !== null

  const stored = readStored()
  const value: DateRange = hasUrlRange ? { from: fromUrl ?? '', to: toUrl ?? '' } : stored

  // Entered without parameters but a period is remembered: put it in the URL
  // so reloads, the back button and outgoing links all agree.
  useEffect(() => {
    if (!hasUrlRange && (stored.from || stored.to)) {
      setParams((current) => withRange(current, stored), { replace: true })
    }
  }, [hasUrlRange, stored.from, stored.to, setParams])

  function set(next: DateRange) {
    writeStored(next)
    setParams((current) => withRange(current, next))
  }

  return [value, set]
}

/** Obdobie do adresy - ostatné parametre (napr. `tab` záložky) ostanú. */
function withRange(current: URLSearchParams, range: DateRange) {
  const next = new URLSearchParams(current)
  next.delete('from')
  next.delete('to')
  if (range.from || range.to) {
    next.set('from', range.from)
    next.set('to', range.to)
  }
  return next
}

/** „Rátať: len odučené lekcie“ - v adrese (`?taught=1`), aby ho odkaz
 *  z prehľadu preniesol do výplatného listu a späť. */
export function useTaughtOnly(): [boolean, (next: boolean) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get('taught') === '1'
  const set = (next: boolean) =>
    setParams(
      (current) => {
        const copy = new URLSearchParams(current)
        if (next) copy.set('taught', '1')
        else copy.delete('taught')
        return copy
      },
      { replace: true },
    )
  return [value, set]
}
